import * as THREE from "three";
import SceneGeometry from "../sceneObject/SceneGeometry";
import ChecklistItemData from "../sceneObjectData/ChecklistItemData";
import ChecklistItemTypeData from "../sceneObjectData/ChecklistItemTypeData";
import SceneObjectData from "../sceneObjectData/SceneObjectData";

// Pin dimensions in scene units (1 unit = 1000 mm)
const NEEDLE_LENGTH = 0.10;   // 100 mm
const NEEDLE_RADIUS = 0.003;  // 3 mm
const SPHERE_RADIUS = 0.035;  // 35 mm (70 mm diameter)

const NEEDLE_COLOR = 0x9e9e9e; // Grey 500
const SPHERE_COLOR = 0xd32f2f; // Material-red 700

export default class ChecklistItemSubGeometry3d extends SceneGeometry<ChecklistItemData> {
    private needleMesh: THREE.Mesh;
    private sphereMesh: THREE.Mesh;
    private needleMaterial: THREE.MeshStandardMaterial;
    private sphereMaterial: THREE.MeshStandardMaterial;

    constructor() {
        super();

        this.needleMaterial = new THREE.MeshStandardMaterial({
            color: NEEDLE_COLOR,
            metalness: 0.85,
            roughness: 0.25,
        });

        this.sphereMaterial = new THREE.MeshStandardMaterial({
            color: SPHERE_COLOR,
            metalness: 0.3,
            roughness: 0.4,
        });

        // Needle: sharp tip at local origin (surface contact point), widens toward the sphere.
        // radiusTop (+Y, toward sphere) is wide; radiusBottom (-Y, tip) is narrow.
        const needleGeo = new THREE.CylinderGeometry(NEEDLE_RADIUS, NEEDLE_RADIUS * 0.3, NEEDLE_LENGTH, 8);
        needleGeo.translate(0, NEEDLE_LENGTH / 2, 0);
        this.needleMesh = new THREE.Mesh(needleGeo, this.needleMaterial);

        // Sphere head sits on top of the needle, away from the surface.
        const sphereGeo = new THREE.SphereGeometry(SPHERE_RADIUS, 16, 12);
        sphereGeo.translate(0, NEEDLE_LENGTH + SPHERE_RADIUS, 0);
        this.sphereMesh = new THREE.Mesh(sphereGeo, this.sphereMaterial);

        this.group.add(this.needleMesh);
        this.group.add(this.sphereMesh);
    }

    protected updateGeometry(data: SceneObjectData, _forceUpdate: boolean): boolean {
        const checklistData = data as ChecklistItemData;
        this.group.position.copy(checklistData.position);

        // Orient the pin so local +Y aligns with the surface normal.
        // Needle tip is at origin, sphere is in +Y — so the sphere floats away from the surface.
        const up = new THREE.Vector3(0, 1, 0);
        const normal = checklistData.normal.clone().normalize();
        this.group.quaternion.setFromUnitVectors(up, normal);

        const typeData = checklistData.sceneObjectTypeData as ChecklistItemTypeData | null;
        this.sphereMaterial.color.set(typeData?.color ?? SPHERE_COLOR);

        this.data = checklistData.clone();
        return true;
    }
}
